/**
 * Splits a text into plain stretches, note names and terms of the glossary, so that
 * the last two can be set apart and explained.
 *
 * Two things are recognised:
 * - music tokens, `{{note:C4}}` or `{{term:staff}}`, which authors write on purpose;
 * - the words of the glossary themselves: each term's name and its listed forms,
 *   and the signs ♯ ♭ ♮ 𝄪 𝄫, wherever they stand in running text.
 *
 * Bare note letters ("C", "до") are never guessed at: they are ordinary words far
 * too often. A note is marked when the text names it with a token.
 */
import type { Glossary } from '@/shared/api/types';

export type TermKind = 'interval' | 'chord' | 'scale' | 'duration' | 'term';
export type TermCategory = NonNullable<Glossary['terms']>[string]['category'];

/** One entry of the glossary, whichever section it comes from. */
export interface TermRef {
  /** The token that names it: "interval:m3", "term:sharp". */
  key: string;
  kind: TermKind;
  id: string;
  name: string;
  short?: string;
  definition?: string;
  /** Only entries of the `terms` section have one. */
  category?: TermCategory;
  forms: string[];
}

export type Segment =
  | { kind: 'text'; text: string }
  | { kind: 'note'; text: string }
  | { kind: 'term'; text: string; term: TermRef };

export type Segmenter = (text: string) => Segment[];

const SECTIONS = [
  ['interval', 'intervals'],
  ['chord', 'chords'],
  ['scale', 'scales'],
  ['duration', 'durations'],
  ['term', 'terms'],
] as const;

/** Every entry of a glossary, section by section, in the order the glossary lists them. */
export function listTerms(glossary: Glossary | undefined): TermRef[] {
  if (!glossary) return [];
  return SECTIONS.flatMap(([kind, section]) =>
    Object.entries(glossary[section] ?? {}).map(([id, entry]) => ({
      key: `${kind}:${id}`,
      kind,
      id,
      name: entry.name,
      short: entry.short ?? undefined,
      definition: entry.definition ?? undefined,
      category: 'category' in entry ? (entry.category as TermCategory) : undefined,
      forms: entry.forms ?? [],
    })),
  );
}

const EMBEDDED_RE = /\{\{\s*([a-z]+:[^}\s]+)\s*\}\}/g;
const WORD_CHAR = /[\p{L}\p{N}]/u;
const HAN = /\p{Script=Han}/u;
const NOT_A_WORD_BEFORE = '(?<![\\p{L}\\p{N}])';
const NOT_A_WORD_AFTER = '(?![\\p{L}\\p{N}])';
/** How many letters a "*" stands for at most: an ending, not another word. */
const ENDING = '\\p{L}{0,3}';

function escape(literal: string): string {
  return (
    literal
      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      // "е" and "ё" are the same letter to a reader, and texts mix them freely.
      .replace(/[её]/gi, (letter) => (letter === letter.toLowerCase() ? '[её]' : '[ЕЁ]'))
  );
}

/**
 * The regular expression source for one spelling of a term.
 *
 * A spelling made of letters must stand as a whole word; a sign (♯) is found
 * anywhere, and so is a spelling in Chinese, which puts no spaces between words.
 */
function spellingPattern(form: string): string {
  const words = form.split(' ').map((word) => {
    const open = word.endsWith('*');
    return escape(open ? word.slice(0, -1) : word) + (open ? ENDING : '');
  });
  const bare = form.replaceAll('*', '');
  const spaced = !HAN.test(bare);
  const before = spaced && WORD_CHAR.test(bare[0] ?? '') ? NOT_A_WORD_BEFORE : '';
  const after =
    spaced && (form.endsWith('*') || WORD_CHAR.test(bare.at(-1) ?? '')) ? NOT_A_WORD_AFTER : '';
  // A space in a form is any single space of the text, a non-breaking one included.
  return before + words.join('[ \\u00a0]') + after;
}

interface Spelling {
  term: TermRef;
  length: number;
  source: string;
  whole: RegExp;
}

function spellingsOf(terms: TermRef[]): Spelling[] {
  const spellings: Spelling[] = [];
  for (const term of terms) {
    for (const form of new Set([term.name, ...term.forms])) {
      if (!form.trim()) continue;
      const source = spellingPattern(form);
      spellings.push({
        term,
        length: form.length,
        source,
        whole: new RegExp(`^(?:${source})$`, 'iu'),
      });
    }
  }
  // The longest first: "major second" is the interval, not the word "major" and a leftover.
  return spellings.sort((a, b) => b.length - a.length);
}

interface Formatting {
  note: (pitch: string) => string;
}

/**
 * A function that splits texts of one language. Building it compiles the glossary
 * once; each call is then a single pass over the text.
 */
export function createSegmenter(glossary: Glossary | undefined, format: Formatting): Segmenter {
  const terms = listTerms(glossary);
  const byKey = new Map(terms.map((term) => [term.key, term]));
  const spellings = spellingsOf(terms);
  const anywhere =
    spellings.length > 0
      ? new RegExp(spellings.map((spelling) => spelling.source).join('|'), 'giu')
      : null;
  const found = new Map<string, TermRef | null>();

  const termOf = (matched: string): TermRef | null => {
    const known = found.get(matched);
    if (known !== undefined) return known;
    const term = spellings.find((spelling) => spelling.whole.test(matched))?.term ?? null;
    found.set(matched, term);
    return term;
  };

  const words = (text: string, out: Segment[]) => {
    if (!text) return;
    if (!anywhere) {
      out.push({ kind: 'text', text });
      return;
    }
    let from = 0;
    for (const match of text.matchAll(anywhere)) {
      const term = termOf(match[0]);
      if (!term) continue;
      if (match.index > from) out.push({ kind: 'text', text: text.slice(from, match.index) });
      out.push({ kind: 'term', text: match[0], term });
      from = match.index + match[0].length;
    }
    if (from < text.length) out.push({ kind: 'text', text: text.slice(from) });
  };

  const token = (name: string, out: Segment[]) => {
    const separator = name.indexOf(':');
    const value = name.slice(separator + 1);
    if (name.slice(0, separator) === 'note') {
      out.push({ kind: 'note', text: format.note(value) });
      return;
    }
    const term = byKey.get(name);
    // A token the glossary does not know is shown as it was written, like any other text.
    out.push(term ? { kind: 'term', text: term.name, term } : { kind: 'text', text: value });
  };

  return (text) => {
    const out: Segment[] = [];
    let from = 0;
    for (const match of text.matchAll(EMBEDDED_RE)) {
      words(text.slice(from, match.index), out);
      token(match[1]!, out);
      from = match.index + match[0].length;
    }
    words(text.slice(from), out);
    return out;
  };
}

/** The address of a term on the glossary page: "interval:m3" is "#interval-m3". */
export const termAnchor = (key: string) => key.replace(':', '-');
